<template>
  <div @contextmenu.stop.prevent>
    <div
      v-if="errorMessage"
      class="q-ma-xl text-bold text-red"
    >
      Error: {{ errorMessage }}
    </div>
    <highcharts
      v-else
      ref="chart"
      :options="highchartOptions"
      class="full-width"
    />
    <report-table-cell-menu
      ref="reportTableCellMenu"
      :report-element="reportElement"
      :report-table-column="outputColumn"
      :cell="selectedPoint"
      @edit-filter="field => editFilters(field)"
      @preview-changes="previewChanges()"
      @show-source="cell => $emit('show-source', cell)"
    />
  </div>
</template>

<script>

import { DEFAULT_CHART_OPTIONS } from 'src/models/report/ReportChart'
import { isComponentSourceCents } from 'src/models/report/ReportElement'
import ReportTableCellMenu from 'src/modules/admin/report-table/ReportTableCellMenu.vue'

export default {
  name: 'AggregateChart',
  components: {
    ReportTableCellMenu
  },
  props: {
    chart: {
      type: Object,
      required: true
    },
    reportElement: {
      type: Object,
      required: true
    },
    reportOptions: {
      type: Object,
      required: true
    }
  },
  emits: ['show-source'],
  data () {
    return {
      categories: [],
      series: [],
      selectedPoint: {},
      selectedColumn: {}
    }
  },
  computed: {
    errorMessage () {
      if (this.reportOptions.group_by.length === 0) return 'Chart requires grouping'

      if (this.reportOptions.group_by.length > 2) return 'Chart only supports up to two grouping: ' + this.reportOptions.group_by.length

      return null
    },
    singleGrouping () {
      return this.reportOptions.group_by.length === 1
    },

    outputColumn () {
      return _.find(this.reportElement.columns, { field: this.reportElement.finalOptions.output_type })
    },
    isMoneyOutput () {
      return this.outputColumn && _.includes(this.outputColumn.component, 'money')
    },
    isPercentOutput () {
      return this.outputColumn && _.includes(this.outputColumn.component, 'percent')
    },
    outputLabel () {
      let label = this.outputColumn.label
      if (this.isMoneyOutput) {
        label += ` (${this.currentCircuit?.localeCurrencySymbol || '$'})`
      } else if (this.isPercentOutput) {
        label += ' (%)'
      }
      return label
    },
    highchartOptions () {
      return {
        ...DEFAULT_CHART_OPTIONS(),
        chart: {
          type: this.reportElement.finalOptions.chart_display_type || 'column',
          zoomType: 'x',
          resetZoomButton: {
            position: {
              align: 'right',
              verticalAlign: 'top',
              x: -10,
              y: 5
            },
            relativeTo: 'chart'
          },
          panning: true,
          panKey: 'shift',
          height: this.reportOptions.height
        },
        plotOptions: {
          column: {
            colorByPoint: this.singleGrouping
          },
          series: {
            point: {
              events: {
                click: (event) => {
                  this.selectedPoint.groupValue = event.point.category
                  this.selectedPoint.value = event.point.options.y
                  this.selectedPoint.col = this.outputColumn
                  event.stopPropagation()
                  event.preventDefault()
                  this.$refs.reportTableCellMenu.show(event)
                  // this.selectedPointName = event.point.name
                  // this.selectedPointCategory = event.point.category
                }
              }
            }
          }
        },
        // colors: _.map(this.chartData, 'color'),
        series: this.series,
        legend: {
          enabled: this.reportOptions.show_legend
        },
        xAxis: {
          categories: this.categories
        },
        yAxis: {
          title: {
            text: this.outputLabel
          },
          min: 0
        }
      }
    }
  },
  watch: {
    chart () {
      this.validateSeries()
    }
  },
  methods: {
    validateSeries () {
      this.series = this.computeSeries()
    },
    computeSeries () {
      const rows = this.chart.chartData.table_data
      let factor = 1
      if (this.outputColumn && isComponentSourceCents(this.outputColumn.component)
      ) {
        factor = 100
      }
      let data
      if (this.reportOptions.group_by.length === 1) {
        data = _.map(rows, row => {
          const groupValue = row[this.reportOptions.group_by[0]]
          let sortValue
          if (this.reportElement.finalOptions.sort_by) {
            sortValue = row[this.reportElement.finalOptions.sort_by] || groupValue
          } else { // If no sort by specified, use group value
            sortValue = groupValue
          }

          sortValue = sortValue.sort_value || (sortValue.hasValue ? sortValue.value : sortValue)

          return {
            name: groupValue.value || groupValue,
            color: groupValue.color || '#0004',
            y: row[this.reportElement.finalOptions.output_type] / factor,
            sortValue
          }
        })

        if (this.reportElement.finalOptions.top_n) {
          data = _.sortBy(data, point => -(point.y || 0))
          data = data.splice(0, this.reportElement.finalOptions.top_n)
        } else if (this.reportElement.finalOptions.sort_by) {
          data = _.sortBy(data, point => point.sortValue)
          if (this.reportElement.finalOptions.descending) {
            data = data.reverse()
          }
        } else {
          data = _.sortBy(data, point => point.sortValue)
        }

        this.categories = _.map(data, 'name')

        const series = {
          name: this.outputColumn.label,
          colorByPoint: true,
          data
        }
        if (this.isMoneyOutput) {
          series.tooltip = {
            valueDecimals: 2,
            valuePrefix: this.currentCircuit?.localeCurrencySymbol || '$'
          }
        } else if (this.isPercentOutput) {
          series.tooltip = {
            valueDecimals: 0,
            valueSuffix: ' %'
          }
        }

        return [series]
      } else if (this.reportOptions.group_by.length === 2) {
        const categoryField = this.reportOptions.group_by[0]
        const categoryValues = _(rows).map(categoryField).uniqBy(point => point?.value || point).value()

        const seriesField = this.reportOptions.group_by[1]
        const seriesValues = _(rows).map(seriesField).uniqBy(point => point?.value || point).value()

        const seriesArray = []

        _.each(seriesValues, seriesValue => {
          const series = {
            name: seriesValue.value || seriesValue,
            color: seriesValue.color || '#0004',
            data: []
          }
          if (this.isMoneyOutput) {
            series.tooltip = {
              valueDecimals: 2,
              valuePrefix: this.currentCircuit?.localeCurrencySymbol || '$'
            }
          } else if (this.isPercentOutput) {
            series.tooltip = {
              valueDecimals: 0,
              valueSuffix: ' %'
            }
          }
          seriesArray.push(series)
          _.each(categoryValues, categoryValue => {
            series.data.push(
              _(rows)
                .filter(row =>
                  row[categoryField].value === categoryValue.value &&
                  row[seriesField].value === seriesValue.value
                )
                .map(this.reportElement.finalOptions.output_type)
                .map(y => y / factor)
                .value()
            )
          })
        })

        this.categories = _.map(categoryValues, categoryValue => categoryValue?.value || categoryValue)

        return seriesArray
      } else {
        return []
      }
    }
  },
  configuration: ['currentCircuit']
}
</script>

<style lang="scss" scoped>
</style>
